import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { MessageTranslator } from '@oneflowab/pomes';
import { push } from 'connected-react-router';
import { useT } from '@transifex/react';

import automationRulesReducer from '@app/reducers/entities/automation-rules';

import LockIcon from '@app/components/icons/lock';
// eslint-disable-next-line import/named
import { Table } from '@app/components/table';
import ActionBar from '@app/components/action-bar';
import ActionsMenu from '@app/components/actions-menu';
import adminPage from '@app/hocs/admin-page';
import Button from '@app/components/button';
import Add from '@app/components/icons/add';
import Pagination from '@app/components/pagination';
import DeleteAutomationRule from '@app/components/modals/delete-automation-rule';
import Tooltip from '@app/components/tooltip';
import { T } from '@app/components/translation';

import { LocalizedDateTime } from '@app/components/localized-date-time';
import { Link } from 'react-router-dom';

import { checkAcl } from '@flow/libs/utils/acl';
import { DeleteMenuItem } from '@app/components/menu-items/delete';
import { EditMenuItem } from '@app/components/menu-items/edit';
import { AutomationRuleName } from '@app/components/automation-rule-name';
import { ActionBarText } from '@app/components/action-bar-text';
import AddAutomationRuleModal from '@app/components/modals/add-automation-rule';

import AutomationRuleSubRoutes from './automation-rule';
import style from './automation-rules.module.scss';
import { getAutomationRulesKeyMessageMap } from '@app/components/automation-rule-name/automation-rules';

export type Props = {
  message: MessageTranslator;
  match: {
    params: {
      id: string;
    };
  };
};

const QUERY_NAME = 'automation-rules';

export const AutomationRulesComponent = ({ message, match }: Props) => {
  const dispatch = useDispatch();
  const t = useT();
  const workspaceId = match.params.id;
  const automationRulesQuery = useSelector((state) =>
    automationRulesReducer.getQuerySelector(state, {
      name: QUERY_NAME,
    }),
  );

  const automationRules = useSelector((state) =>
    automationRulesReducer.getAutomationRulesSelector(state, {
      ids: automationRulesQuery.result,
    }),
  );

  useEffect(() => {
    dispatch(
      automationRulesReducer.queryAutomationRules({
        name: QUERY_NAME,
        params: {
          workspaceId,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    workspaceId,
  ]);

  const handleOnEditClick = (automationRule: Oneflow.AutomationRule) => () => {
    const { id } = automationRule;

    // TODO: Rename to automation-rules when ready
    const editLink = `/admin/workspaces/${workspaceId}/data-retention/${id}/details?edit=true`;
    dispatch(push(editLink));
  };

  const getActions = (automationRule: Oneflow.AutomationRule) => {
    const { id } = automationRule;

    const hasRemovePermission = checkAcl(automationRule.acl, 'automation_rule:remove');
    const hasEditPermission = checkAcl(automationRule.acl, 'automation_rule:update');

    if (!hasRemovePermission && !hasEditPermission) {
      return (
        <>
          <Tooltip
            message={
              <T
                _str="This rule cannot be modified"
                _comment="Tooltip showing reason for not being allowed to edit or delete the rule."
              />
            }
            side="top"
          >
            <ActionsMenu
              actions={[]}
              disabled
              focusOnCloseDisabled
            />
          </Tooltip>
        </>
      );
    }

    return (
      <ActionsMenu
        actions={[
          <EditMenuItem
            key={`edit-${id}`}
            disabled={!hasEditPermission}
            onClick={handleOnEditClick(automationRule)}
          />,
          <DeleteAutomationRule
            automationRule={automationRule}
            key={`delete-${id}`}
          >
            {(onClick: () => void) => (
              <DeleteMenuItem
                onClick={onClick}
                disabled={!hasRemovePermission}
              />
            )}
          </DeleteAutomationRule>,
        ]}
        focusOnCloseDisabled
      />
    );
  };

  const getName = (automationRule: Oneflow.AutomationRule) => {
    const { id, system } = automationRule;

    // TODO: Rename to automation-rules when ready
    return (
      <span className={style.IconContainer}>
        <Link
          key={id}
          to={`/admin/workspaces/${workspaceId}/data-retention/${id}`}
        >
          <AutomationRuleName automationRule={automationRule} />
        </Link>
        {system ? <LockIcon height="14px" /> : ''}
      </span>
    );
  };

  const getActive = (automationRule: Oneflow.AutomationRule) => {
    const { active } = automationRule;

    if (active) {
      return (
        <T
          _str="Active"
          _comment="Text explaining that the api token is active."
        />
      );
    }

    return (
      <T
        _str="Inactive"
        _comment="Text explaining that the api token is inactive."
      />
    );
  };

  const getDays = (automationRule: Oneflow.AutomationRule) => (
    <T
      _str="{count, plural,
        one {{count} day}
        other {{count} days}
      }"
      _comment="Explains how many days the automation rule will trigger after."
      count={automationRule.days}
    />
  );

  const getCreatedTime = (automationRule: Oneflow.AutomationRule) => (
    <LocalizedDateTime datetime={automationRule.createdTime} />
  );

  const getUpdatedTime = (automationRule: Oneflow.AutomationRule) => (
    <LocalizedDateTime datetime={automationRule.updatedTime} />
  );

  const getUserTableConfig = () => ({
    items: automationRules,
    itemKey: 'id',
    columns: [
      {
        name: 'rule',
        label: (
          <T
            _str="Rule"
            _comment="Used as the label of the relevant field in the automation rules page"
          />
        ),
        type: 'cell',
        value: getName,
      },
      {
        name: 'active',
        label: (
          <T
            _str="Active"
            _comment="Used as the label of the relevant field in the automation rules page"
          />
        ),
        type: 'cell',
        value: getActive,
      },
      {
        name: 'days',
        label: (
          <T
            _str="Trigger after"
            _comment="Used as the label of the relevant field in the automation rules page"
          />
        ),
        type: 'cell',
        value: getDays,
      },
      {
        name: 'createdTime',
        label: (
          <T
            _str="Created"
            _comment="Used as the label of the relevant field in the automation rules page"
          />
        ),
        type: 'cell',
        value: getCreatedTime,
      },
      {
        name: 'updatedTime',
        label: (
          <T
            _str="Updated"
            _comment="Used as the label of the relevant field in the automation rules page"
          />
        ),
        type: 'cell',
        value: getUpdatedTime,
      },
      {
        name: 'actions',
        label: (
          <T
            _str="Actions"
            _comment="Used as the label of the relevant field in the automation rules page"
          />
        ),
        type: 'actions',
        value: getActions,
      },
    ],
  });

  const handlePageChange = (pagination): void => {
    dispatch(
      automationRulesReducer.queryAutomationRules({
        name: QUERY_NAME,
        pagination,
        params: {
          workspaceId,
        },
      }),
    );
  };

  const getAvailableKeys = () => {
    const automationRulesKeyMessageMap = getAutomationRulesKeyMessageMap(t);
    const automationRulesKeys = Object.keys(automationRulesKeyMessageMap);
    const activeRuleKeys = automationRules.map((rule) => rule.key);
    const filteredKeyOptions = automationRulesKeys.filter((key) => !activeRuleKeys.includes(key));
    return filteredKeyOptions.map((key) => ({
      value: key,
      label: automationRulesKeyMessageMap[key],
    }));
  };

  const getChildren = (onClick: () => void) => {
    return (
      <Button
        icon={Add}
        kind="primary"
        customClass={style.ActionButton}
        onClick={onClick}
      >
        <T
          _str="Create rule"
          _comment="Button label."
        />
      </Button>
    );
  };

  return (
    <div className={style.AutomationRules}>
      <ActionBar collapsed>
        <ActionBar.Group>
          <div className={style.Buttons}>
            <AddAutomationRuleModal
              message={message}
              workspaceId={workspaceId}
              availableKeys={getAvailableKeys()}
            >
              {getChildren}
            </AddAutomationRuleModal>
            <ActionBarText
              header={
                <T
                  _str="Data retention policies let you set the retention period for your documents."
                  _comment="Used as the title of the Workspace Automation Rules page"
                />
              }
              text={
                <T
                  _str="This can be useful for GDPR purposes or to align with your company policies."
                  _comment="Help text for the settings on the Automation Rules page."
                />
              }
            />
          </div>
        </ActionBar.Group>
      </ActionBar>
      <div>
        <Table
          config={getUserTableConfig()}
          query={automationRulesQuery}
        />
        <Pagination
          totalItems={automationRulesQuery.count}
          itemsPerPage={automationRulesQuery.pagination.limit}
          currentOffset={automationRulesQuery.pagination.offset}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};

type MapperProps = {
  message: MessageTranslator;
};

export const propsMapper = ({ props: { message } }: { props: MapperProps }) => ({
  title: message({
    id: 'Data retention', // TODO: Rename to Automation Rules when ready
    comment: 'Used as the title of the Data Retention page',
  }),
  modules: [
    [
      {
        path: '/:automationRuleId(\\d+)',
        title: '',
        component: AutomationRuleSubRoutes,
      },
    ],
  ],
});

export const AutomationRules = adminPage(propsMapper)(AutomationRulesComponent);
